// ─── demandeFacturation.js ───────────────────────────────────────────
// Preparer ce qu'Anissa envoie a Benoit pour qu'il facture.
//
// Anissa soigne, Benoit facture. Depuis le 08.08 le SaaS est
// mono-praticienne et le compte de Benoit y a ete supprime : il ne peut pas
// venir lire le cockpit. Ce module construit l'instantane qui part dans la
// file `facturation_demandes`, qu'une petite application locale chez lui
// vient relire.
//
// ⚠ AUCUNE DONNEE CLINIQUE NE DOIT SORTIR PAR ICI.
//
// Le libelle est construit a partir du PACK et de rien d'autre. Il ne doit
// jamais nommer une analyse, un motif ou une pathologie. Reprendre les
// libelles du plan serait le geste naturel et exactement la faute : une
// facture qui dit « Cycle du cortisol salivaire » raconte quelque chose de
// la cliente a quiconque la voit passer. Un test verrouille ce point.
//
// C'est le miroir de l'anonymiseur PDF : lui retire l'identite pour
// proteger la cliente, celui-ci ne transporte que l'identite et l'argent.

import { PACK_DEFINITIONS } from './packSystem';

/** Bornes de bon sens sur le fractionnement. */
export const MENSUALITES_MIN = 1;
export const MENSUALITES_MAX = 12;

const texte = (v) => (typeof v === 'string' ? v.trim() : '');
const nombre = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const arrondi2 = (n) => Math.round(n * 100) / 100;

/**
 * Le libelle qui figurera sur la facture.
 *
 * Construit UNIQUEMENT depuis le pack. Aucune entree du plan n'y entre.
 *
 * @param {string} packType
 * @param {boolean} avecAnalyses
 * @returns {string}
 */
export function libelleCommercial(packType, avecAnalyses = false) {
  const base = PACK_DEFINITIONS[packType]?.label || 'Accompagnement nutritionnel';
  return avecAnalyses ? `${base} + analyses` : base;
}

/**
 * Construit la demande de facturation, ou refuse en disant pourquoi.
 *
 * @param {{
 *   client: object,
 *   plan?: object|null,
 *   mensualites?: number,
 *   facturerPack?: boolean,
 * }} entree
 * @returns {{ok: true, demande: object, avertissements: string[]} | {ok: false, raison: string}}
 */
export function construireDemande({
  client, plan = null, mensualites = 1, facturerPack = true,
} = {}) {
  if (!client?.id) return { ok: false, raison: 'client_absent' };

  const form = client.form || {};
  const nom = texte(form.nom) || texte(client.nom);
  const prenom = texte(form.prenom) || texte(client.prenom);
  // Sans identite, la facture ne peut pas exister. C'est le seul refus dur :
  // tout le reste se rattrape cote Benoit.
  if (!nom || !prenom) return { ok: false, raison: 'identite_absente' };

  // On REFUSE un decimal au lieu de le tronquer. « 2.5 paiements » n'a pas
  // de sens : tronquer a 2 accepterait silencieusement une saisie fausse et
  // enverrait un echeancier que personne n'a voulu.
  const n = Number(mensualites);
  if (!Number.isInteger(n) || n < MENSUALITES_MIN || n > MENSUALITES_MAX) {
    return { ok: false, raison: 'mensualites_invalides' };
  }

  const packType = client.pack_type || plan?.pack_type || null;
  const prixPackBrut = facturerPack ? nombre(PACK_DEFINITIONS[packType]?.price) : 0;

  // ─── La remise ──────────────────────────────────────────────────
  //
  // ⚠ ELLE PORTE SUR LE PACK, JAMAIS SUR LES ANALYSES. Doctrine du 05.08 :
  // le temps et l'expertise se negocient, le cout labo non. Anissa avance
  // cet argent au laboratoire ; une remise dessus n'est pas un geste
  // commercial, c'est une perte seche.
  const remise = facturerPack ? nombre(client.remise_chf) : 0;
  const motifRemise = texte(client.remise_motif);
  // Une remise plus grande que ce qu'elle reduit n'a pas de sens : c'est une
  // saisie fausse. On REFUSE plutot que de la rogner en silence, sinon la
  // facture partirait avec un montant que personne n'a voulu.
  if (remise > prixPackBrut) return { ok: false, raison: 'remise_superieure_au_pack' };
  // Le motif est deja impose en base ; on le revoit ici parce qu'une fiche
  // ancienne ou une ecriture hors contrainte ne doit pas produire une
  // facture amputee sans explication.
  if (remise > 0 && motifRemise.length < 3) return { ok: false, raison: 'remise_sans_motif' };

  const prixPack = arrondi2(prixPackBrut - remise);
  // Le montant a refacturer est celui ENREGISTRE au moment de la
  // prescription. On ne le RECALCULE jamais depuis le catalogue : c'est
  // precisement le defaut qui a coute 131.10 CHF sur le cycle de Clelia,
  // parce que le catalogue avait bouge entre la prescription et la facture.
  //
  // Repli pour les plans anterieurs au 25.08, qui n'ont pas la colonne :
  // `total_cost_anissa_chf` moins le credit du pack. Ce n'est PAS un
  // recalcul — les deux valeurs sont des instantanes pris a la
  // prescription, exactement comme la colonne manquante l'aurait ete.
  // C'est aussi ce que la carte du plan affiche deja, donc les deux
  // chiffres concordent au lieu de se contredire.
  // Cas reel : le plan de Stephanie du 24.08, supplement 61 CHF.
  const credit = nombre(PACK_DEFINITIONS[client.pack_type || plan?.pack_type]?.includedAnalysisCreditChf);
  let prixAnalyses = 0;
  let analysesDeduites = false;
  if (plan?.cost_for_client_chf != null) {
    prixAnalyses = nombre(plan.cost_for_client_chf);
  } else if (plan?.total_cost_anissa_chf != null) {
    prixAnalyses = Math.max(0, arrondi2(nombre(plan.total_cost_anissa_chf) - credit));
    analysesDeduites = true;
  }

  // Ce que le LABO a coute, a ne pas confondre avec ce que la cliente paie :
  // la difference est le credit du pack qu'Anissa absorbe. Sans ce chiffre,
  // le recap mensuel ne peut pas dire ce qui lui revient vraiment.
  const coutLabo = plan?.total_cost_anissa_chf != null
    ? arrondi2(nombre(plan.total_cost_anissa_chf))
    : null;

  const total = arrondi2(prixPack + prixAnalyses);
  if (total <= 0) return { ok: false, raison: 'rien_a_facturer' };

  const avertissements = [];
  const adresse = texte(form.adresse);
  // On n'empeche PAS l'envoi : c'est Benoit qui facture, donc c'est lui qui
  // reclamera l'adresse. Bloquer Anissa la-dessus serait lui rendre un
  // probleme qui n'est pas le sien.
  if (!adresse) avertissements.push('adresse_manquante');
  if (analysesDeduites) avertissements.push('montant_analyses_deduit');
  if (plan && plan.cost_for_client_chf == null && plan.total_cost_anissa_chf == null
      && (plan.selected_tests || []).length > 0) {
    avertissements.push('montant_analyses_introuvable');
  }
  if (plan && !plan.demande_controlee_le && !plan.bons_confirmes_le) {
    avertissements.push('commande_non_controlee');
  }

  return {
    ok: true,
    avertissements,
    demande: {
      client_id: client.id,
      analysis_plan_id: plan?.id || null,
      nom,
      prenom,
      adresse: adresse || null,
      email: texte(form.email) || texte(client.email) || null,
      telephone: texte(form.telephone) || null,
      libelle: libelleCommercial(packType, prixAnalyses > 0),
      montant_chf: total,
      // Le pack APRES remise : c'est ce qui est du. Le brut se retrouve en
      // ajoutant la remise, qui voyage juste a cote.
      montant_pack_chf: prixPack || null,
      montant_analyses_chf: prixAnalyses || null,
      cout_labo_chf: coutLabo,
      // Le meme cout, laboratoire par laboratoire, pour que le recap annuel
      // puisse dire ce que chaque partenaire represente.
      cout_labo_detail: repartirParLabo(plan),
      // Figes avec la facture : si le motif change sur la fiche plus tard,
      // celle qui est deja partie garde le sien.
      remise_chf: remise || null,
      remise_motif: remise > 0 ? motifRemise : null,
      mensualites: n,
      statut: 'a_faire',
    },
  };
}

/**
 * Ventile le cout labo d'un plan par laboratoire.
 *
 * Ortho et MGD sont deux partenaires distincts, avec des tarifs et des
 * modes de facturation opposes. Le total agrege ne permet aucune
 * comparaison sur une annee.
 *
 * ⚠ Rend `null` plutot qu'un objet vide quand la ventilation est
 * impossible : un objet vide se lirait « aucun laboratoire », ce qui est
 * faux et ferait afficher zero au lieu de « on ne sait pas ».
 *
 * @param {object|null} plan
 * @returns {Record<string, number>|null}
 */
export function repartirParLabo(plan) {
  const tests = Array.isArray(plan?.selected_tests) ? plan.selected_tests : [];
  if (tests.length === 0) return null;

  const parLabo = {};
  for (const t of tests) {
    const labo = t?.source_lab;
    if (!labo) continue;
    parLabo[labo] = (parLabo[labo] || 0) + nombre(t.cost_chf ?? t.cost_anissa_chf);
  }

  // Les frais suivent leur propre labo : une taxe de commande MGD n'a rien
  // a faire dans la colonne d'Ortho.
  for (const l of (plan?.lab_fees?.labs || [])) {
    if (!l?.lab) continue;
    parLabo[l.lab] = (parLabo[l.lab] || 0) + nombre(l.tax) + nombre(l.prelevement);
  }

  const labos = Object.keys(parLabo);
  if (labos.length === 0) return null;
  for (const k of labos) parLabo[k] = arrondi2(parLabo[k]);
  return parLabo;
}

/** Ce qu'on montre a Anissa quand l'envoi est refuse. */
export function messageRefusFacturation(resultat) {
  if (!resultat || resultat.ok) return null;
  switch (resultat.raison) {
    case 'identite_absente':
      return 'Il manque le nom ou le prénom sur cette fiche. Sans ça, Benoit ne peut pas établir la facture.';
    case 'rien_a_facturer':
      return 'Rien à facturer ici : ni pack, ni supplément analyses.';
    case 'mensualites_invalides':
      return 'Le nombre de paiements doit être entre 1 et 12.';
    case 'remise_superieure_au_pack':
      return 'La remise dépasse le prix du pack. Corrige-la sur la fiche : '
        + 'elle ne porte que sur l’accompagnement, jamais sur les analyses.';
    case 'remise_sans_motif':
      // ⚠ Ce cas se produit A CHAQUE SAISIE, entre le moment ou le montant
      // est tape et celui ou la raison l'est. Ce n'est donc pas une faute,
      // c'est une etape. Le message d'origine — « sinon personne ne saura
      // l'expliquer dans six mois » — etait juste sur le fond et alarmant
      // au mauvais moment.
      return 'Encore la raison de la remise, et c’est bon.';
    case 'client_absent':
      return 'Aucune fiche cliente.';
    default:
      return 'L’envoi a échoué.';
  }
}

/** Les avertissements, en clair, pour qu'Anissa décide d'envoyer ou non. */
export function messageAvertissement(code) {
  switch (code) {
    case 'adresse_manquante':
      return 'L’adresse postale manque sur la fiche. Benoit la verra signalée et te la demandera.';
    case 'montant_analyses_deduit':
      return 'Ce plan date d’avant l’enregistrement du montant : le supplément analyses est déduit du coût enregistré à la prescription. Vérifie qu’il correspond à ce que le labo a facturé.';
    case 'montant_analyses_introuvable':
      return 'Impossible de chiffrer les analyses de ce plan : seul le pack part, le supplément est à ajouter à la main.';
    case 'commande_non_controlee':
      return 'La commande du labo n’a pas encore été contrôlée. Le montant des analyses peut ne pas être le bon.';
    default:
      return null;
  }
}
